// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"dashboard@4105f63fb3a9"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "15.5.14";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:locale/survey\",\"regex\":\"^/([^/]+)/survey$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:prismSlug*\",\"regex\":\"^/([^/]+)/(.+)$\",\"paramNames\":[\"locale\",\"prismSlug\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/cabin\",\"regex\":\"^/([^/]+)/([^/]+)/cabin$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/checklist\",\"regex\":\"^/([^/]+)/([^/]+)/checklist$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/contact\",\"regex\":\"^/([^/]+)/([^/]+)/contact$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/good-to-know\",\"regex\":\"^/([^/]+)/([^/]+)/good-to-know$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/iframe-poc\",\"regex\":\"^/([^/]+)/([^/]+)/iframe-poc$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/internet\",\"regex\":\"^/([^/]+)/([^/]+)/internet$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/journey-highlights\",\"regex\":\"^/([^/]+)/([^/]+)/journey-highlights$\",\"paramNames\":[\"locale\",\"bookingCode\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/:bookingCode/travel-itinerary/:travelDirection\",\"regex\":\"^/([^/]+)/([^/]+)/travel-itinerary/([^/]+)$\",\"paramNames\":[\"locale\",\"bookingCode\",\"travelDirection\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/archive\",\"regex\":\"^/([^/]+)/archive$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/go/:slug*\",\"regex\":\"^/([^/]+)/go/(.+)$\",\"paramNames\":[\"locale\",\"slug\"],\"hasOptionalPrefix\":true},{\"path\":\"/:locale/sign-in\",\"regex\":\"^/([^/]+)/sign-in$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true}],\"staticRoutes\":[],\"isrRoutes\":[]}";import * as Sentry from '@sentry/nextjs';
import { getSharedSentryConfig } from '@lib/sentry/config-helpers';

/**
 * This will get executed by the Sentry SDK on Sentry.init and therefor has
 * access to non-public environment variables.
 */
const getSentryClientConfig = (): Sentry.BrowserOptions => {
  const sharedSentryConfig = getSharedSentryConfig();
  const { environment } = sharedSentryConfig;

  return {
    ...sharedSentryConfig,

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: environment === 'staging' ? 1 : 0.001,
    sampleRate: 1,
    beforeSend: event => {
      // force‑send “golden” events
      if (event.tags?.['force-send'] || event.tags?.forceSend) {
        return event;
      }

      // Otherwise apply default sampling rate.

      if (environment === 'staging') {
        return event;
      }

      return Math.random() < 0.2 ? event : null;
    },

    replaysOnErrorSampleRate: environment === 'staging' ? 1 : 0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: environment === 'staging' ? 1 : 0,

    ignoreErrors: [
      'Non-Error promise rejection captured',
      'NetworkError when attempting to fetch resource',
      'Failed to fetch',
      'DgoProfile',
      'An error occurred in the Server Components render. The specific message is omitted',
      'Load failed', // Oft von Adblock Extensions ausgelöst // Nicht app relevant
      "Can't find variable: ServiceWorkerContainer",
      'The operation is insecure.', // Event Notice
      "Unexpected token '?'", // Alte browser
      "Failed to read the 'localStorage' property from 'Window'", // Wenn jemand localStorage deaktivert
      "evaluating 'navigator.serviceWorker.getRegistrations'", // XAPP mag keine ServiceWorker
    ],

    // OM-2616
    denyUrls: [/mouseflow\.com/],

    // If Log support (https://tuic.sentry.io/explore/logs) should be enabled
    enableLogs: false,

    // You can remove this option if you're not planning to use the Sentry Session Replay feature:
    integrations: [
      Sentry.extraErrorDataIntegration(),
      // Sentry.replayIntegration({
      //   // Additional Replay configuration goes in here, for examplee:
      //   maskAllText: true,
      //   blockAllMedia: true,
      // }),
    ],

    // Enable sending user PII (Personally Identifiable Information)
    // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
    // sendDefaultPii: true,
  };
};

Sentry.init(getSentryClientConfig());

// This export will instrument router navigations, and is only relevant if you enable tracing.
// `captureRouterTransitionStart` is available from SDK version 9.12.0 onwards
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
